import { useEffect, useState } from 'react';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';

type Kind = 'ansvar' | 'konto' | 'motpart';

interface KodverkRow {
  ftgId: string;
  companyName: string | null;
  departmentCode: string | null;
  departmentName: string | null;
  code: string;
  text: string | null;
  passiv: boolean;
  giltigTom: string | null;
}

interface KodverkPage {
  snapshotDate: string | null;
  kind: Kind;
  total: number;
  page: number;
  pageSize: number;
  rows: KodverkRow[];
}

const KINDS: Kind[] = ['ansvar', 'konto', 'motpart'];

function withCode(name: string | null, code: string | null): string {
  if (name && code) return `${name} (${code})`;
  return name || code || '–';
}

export default function EconKodverkPage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('ansvar');
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<KodverkPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api
      .get<{ success: boolean; data: KodverkPage }>('/api/admin/econ-kodverk', {
        params: { kind, q: submitted, page },
      })
      .then((response) => {
        if (!cancelled) setData(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setError(t('econKodverk.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, submitted, page, t]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">{t('econKodverk.title')}</h1>
        <p className="text-sm text-textSecondary">
          {data?.snapshotDate
            ? t('econKodverk.snapshot').replace('{date}', data.snapshotDate)
            : t('econKodverk.description')}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {KINDS.map((item) => (
          <button
            key={item}
            type="button"
            className={`rounded px-3 py-1.5 text-sm ${
              item === kind ? 'bg-bgPrimary text-white' : 'border border-border'
            }`}
            onClick={() => {
              setKind(item);
              setPage(1);
            }}
          >
            {t(`econKodverk.kind.${item}`)}
          </button>
        ))}
      </div>

      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setSubmitted(query.trim());
        }}
      >
        <input
          className="w-full max-w-md rounded border border-border bg-bgContainer px-3 py-2 text-sm"
          value={query}
          placeholder={t('econKodverk.searchPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button type="submit" className="rounded bg-bgPrimary px-4 py-2 text-sm text-white">
          {t('common.search')}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-textSecondary">{t('common.loading')}</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !data || data.rows.length === 0 ? (
        <p className="text-sm text-textSecondary">{t('econKodverk.empty')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full text-sm">
              <thead className="bg-bgElevated text-left text-textSecondary">
                <tr>
                  <th className="px-3 py-2">{t('econKodverk.company')}</th>
                  {kind === 'ansvar' && <th className="px-3 py-2">{t('econKodverk.department')}</th>}
                  <th className="px-3 py-2">{t('econKodverk.code')}</th>
                  <th className="px-3 py-2">{t('econKodverk.text')}</th>
                  <th className="px-3 py-2">{t('econKodverk.passive')}</th>
                  <th className="px-3 py-2">{t('econKodverk.validTo')}</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={`${row.ftgId}-${row.code}`} className="border-t border-border">
                    <td className="px-3 py-2">{withCode(row.companyName, row.ftgId)}</td>
                    {kind === 'ansvar' && (
                      <td className="px-3 py-2">{withCode(row.departmentName, row.departmentCode)}</td>
                    )}
                    <td className="px-3 py-2 font-mono">{row.code}</td>
                    <td className="px-3 py-2">{row.text || '–'}</td>
                    <td className="px-3 py-2">{row.passiv ? t('common.yes') : t('common.no')}</td>
                    <td className="px-3 py-2">{row.giltigTom || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button
              type="button"
              className="rounded border border-border px-3 py-1 disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              {t('econKodverk.prev')}
            </button>
            <span>
              {t('econKodverk.page')
                .replace('{page}', String(page))
                .replace('{pages}', String(totalPages))
                .replace('{total}', String(data.total))}
            </span>
            <button
              type="button"
              className="rounded border border-border px-3 py-1 disabled:opacity-50"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              {t('econKodverk.next')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
